<template>
  <v-row>
    <v-breadcrumbs :items="['reports', 'investment performance']" divider="/"></v-breadcrumbs>
  </v-row>

  <!-- input card -->
  <v-row>
    <v-col cols="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">investment performance report</v-card-title>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-row align="center">
            <v-col>
              <v-select
                v-model="selectedBrokerAccounts"
                :items="brokerAccounts"
                item-title="label"
                item-value="id"
                label="broker accounts"
                density="compact"
                variant="outlined"
                hide-details
                multiple
                chips
                closable-chips
              ></v-select>
            </v-col>
            <v-col>
              <v-select
                v-model="selectedFY"
                :items="financialYears"
                label="financial year"
                density="compact"
                variant="outlined"
                hide-details
                clearable
                @update:modelValue="onFYSelect"
              ></v-select>
            </v-col>
            <v-col>
              <v-text-field
                v-model="startDate"
                type="date"
                label="start date"
                density="compact"
                variant="outlined"
                hide-details
                @update:modelValue="selectedFY = null"
              ></v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                v-model="endDate"
                type="date"
                label="end date"
                density="compact"
                variant="outlined"
                hide-details
                @update:modelValue="selectedFY = null"
              ></v-text-field>
            </v-col>
            <v-col cols="auto">
              <v-btn
                @click="getReport"
                :loading="loading"
                :disabled="selectedBrokerAccounts.length === 0 || !startDate || !endDate"
              >go</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- missing price error -->
  <v-row v-if="priceError">
    <v-col cols="12" md="8">
      <v-card :elevation="1" color="error" variant="tonal">
        <v-card-item>
          <v-card-title class="text-left">price data missing</v-card-title>
          <v-card-subtitle class="text-left">{{ priceError.message }}</v-card-subtitle>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <div v-if="priceError.missing_start_prices?.length > 0">
            <div class="text-caption text-uppercase font-weight-bold mb-1">missing start-of-period prices ({{ startDate }})</div>
            <v-chip v-for="t in priceError.missing_start_prices" :key="t" class="mr-1 mb-1" size="small" color="error" variant="tonal">{{ t }}</v-chip>
          </div>
          <div v-if="priceError.missing_end_prices?.length > 0" class="mt-2">
            <div class="text-caption text-uppercase font-weight-bold mb-1">missing end-of-period prices ({{ endDate }})</div>
            <v-chip v-for="t in priceError.missing_end_prices" :key="t" class="mr-1 mb-1" size="small" color="error" variant="tonal">{{ t }}</v-chip>
          </div>
          <div v-if="priceError.missing_start_fx?.length > 0 || priceError.missing_end_fx?.length > 0" class="mt-2">
            <div class="text-caption text-uppercase font-weight-bold mb-1">missing FX rate data</div>
            <v-chip v-for="c in [...(priceError.missing_start_fx || []), ...(priceError.missing_end_fx || [])]" :key="c" class="mr-1 mb-1" size="small" color="error" variant="tonal">{{ c }}</v-chip>
          </div>
          <div class="mt-3 text-caption">navigate to <strong>admin / price backfill</strong> to retrieve historical prices for the missing dates</div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- missing dividend FX warning -->
  <v-row v-if="report && report.missing_dividend_fx && report.missing_dividend_fx.length > 0">
    <v-col cols="12" md="8">
      <v-alert
        type="warning"
        variant="tonal"
        closable
        title="dividend FX rates missing"
      >
        Some dividend payments were excluded from income and IRR because no FX rate was found within 7 days of the ex-date. Run a <strong>price backfill</strong> for the affected dates to include them.
        <div class="mt-2">
          <v-chip v-for="m in report.missing_dividend_fx" :key="m.currency + m.date" size="small" class="mr-1 mb-1" color="warning" variant="tonal">
            {{ m.currency }} — {{ m.date }}
          </v-chip>
        </div>
      </v-alert>
    </v-col>
  </v-row>

  <!-- summary table -->
  <v-row v-if="report">
    <v-col cols="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">summary</v-card-title>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-table density="compact">
            <thead>
              <tr>
                <th></th>
                <th class="text-right">profit</th>
                <th class="text-right">IRR</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-grey text-left">realised</td>
                <td class="text-right" :class="profitClass(report.summary.total_realised_profit)">
                  ${{ formatCurrency(report.summary.total_realised_profit) }}
                </td>
                <td class="text-right font-weight-bold" :class="irrClass(report.summary.realised_irr)">
                  {{ report.summary.realised_irr != null ? report.summary.realised_irr + '%' : '—' }}
                </td>
              </tr>
              <tr>
                <td class="text-grey text-left">unrealised</td>
                <td class="text-right" :class="profitClass(report.summary.total_unrealised_profit)">
                  ${{ formatCurrency(report.summary.total_unrealised_profit) }}
                </td>
                <td class="text-right font-weight-bold" :class="irrClass(report.summary.unrealised_irr)">
                  {{ report.summary.unrealised_irr != null ? report.summary.unrealised_irr + '%' : '—' }}
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td class="text-left">total</td>
                <td class="text-right" :class="profitClass(report.summary.total_period_profit)">
                  ${{ formatCurrency(report.summary.total_period_profit) }}
                </td>
                <td class="text-right" :class="irrClass(report.summary.period_irr)">
                  {{ report.summary.period_irr != null ? report.summary.period_irr + '%' : '—' }}
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- realised positions -->
  <v-row v-if="report">
    <v-col cols="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">realised positions</v-card-title>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <div v-if="report.realised.length === 0" class="pa-6 text-grey text-center">
            no closed positions in this date range
          </div>
          <v-table v-else density="compact">
            <thead>
              <tr>
                <th>ticker</th>
                <th>broker</th>
                <th>ccy</th>
                <th>open at start?</th>
                <th class="text-right">start value</th>
                <th class="text-right">buys</th>
                <th class="text-right">sells</th>
                <th class="text-right">income</th>
                <th class="text-right">franking</th>
                <th class="text-right">profit</th>
                <th class="text-right">IRR</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(pos, i) in sortedRealised" :key="i">
                <td class="font-weight-medium">{{ pos.ticker }}</td>
                <td>{{ brokerAccountLabel(pos.broker_account_id) }}</td>
                <td>{{ pos.currency }}</td>
                <td>
                  <v-chip size="x-small" :color="pos.open_at_start ? 'orange' : 'blue'" variant="tonal">
                    {{ pos.open_at_start ? 'yes' : 'no' }}
                  </v-chip>
                </td>
                <td class="text-right">{{ pos.open_at_start ? formatCurrency(pos.start_value) : '—' }}</td>
                <td class="text-right">{{ pos.buys_in_period > 0 ? formatCurrency(pos.buys_in_period) : '—' }}</td>
                <td class="text-right">{{ formatCurrency(pos.sells_in_period) }}</td>
                <td class="text-right text-green">{{ pos.dividend_income > 0 ? formatCurrency(pos.dividend_income) : '—' }}</td>
                <td class="text-right text-green">{{ pos.franking_credits > 0 ? formatCurrency(pos.franking_credits) : '—' }}</td>
                <td class="text-right font-weight-medium" :class="profitClass(pos.period_profit)">{{ formatCurrency(pos.period_profit) }}</td>
                <td class="text-right font-weight-bold" :class="irrClass(pos.irr)">{{ pos.irr != null ? pos.irr + '%' : '—' }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- unrealised positions -->
  <v-row v-if="report">
    <v-col cols="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">unrealised positions</v-card-title>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <div v-if="report.unrealised.length === 0" class="pa-6 text-grey text-center">
            no open positions as of end date
          </div>
          <v-table v-else density="compact">
            <thead>
              <tr>
                <th>ticker</th>
                <th>broker</th>
                <th>ccy</th>
                <th>open at start?</th>
                <th class="text-right">start price</th>
                <th class="text-right">start value</th>
                <th class="text-right">buys</th>
                <th class="text-right">sells</th>
                <th class="text-right">end price</th>
                <th class="text-right">end value</th>
                <th class="text-right">income</th>
                <th class="text-right">franking</th>
                <th class="text-right">profit</th>
                <th class="text-right">IRR</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(pos, i) in sortedUnrealised" :key="i">
                <td class="font-weight-medium">{{ pos.ticker }}</td>
                <td>{{ brokerAccountLabel(pos.broker_account_id) }}</td>
                <td>{{ pos.currency }}</td>
                <td>
                  <v-chip size="x-small" :color="pos.open_at_start ? 'orange' : 'blue'" variant="tonal">
                    {{ pos.open_at_start ? 'yes' : 'no' }}
                  </v-chip>
                </td>
                <td class="text-right">{{ pos.open_at_start && pos.start_price != null ? pos.start_price.toFixed(4) : '—' }}</td>
                <td class="text-right">{{ pos.open_at_start ? formatCurrency(pos.start_value) : '—' }}</td>
                <td class="text-right">{{ pos.buys_in_period > 0 ? formatCurrency(pos.buys_in_period) : '—' }}</td>
                <td class="text-right">{{ pos.sells_in_period > 0 ? formatCurrency(pos.sells_in_period) : '—' }}</td>
                <td class="text-right">{{ pos.end_price != null ? pos.end_price.toFixed(4) : '—' }}</td>
                <td class="text-right">{{ formatCurrency(pos.end_value) }}</td>
                <td class="text-right text-green">{{ pos.dividend_income > 0 ? formatCurrency(pos.dividend_income) : '—' }}</td>
                <td class="text-right text-green">{{ pos.franking_credits > 0 ? formatCurrency(pos.franking_credits) : '—' }}</td>
                <td class="text-right font-weight-medium" :class="profitClass(pos.period_profit)">{{ formatCurrency(pos.period_profit) }}</td>
                <td class="text-right font-weight-bold" :class="irrClass(pos.irr)">{{ pos.irr != null ? pos.irr + '%' : '—' }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

</template>

<script>
import axios from 'axios';
import { mapState, mapMutations } from 'vuex';

export default {
  data() {
    return {
      brokerAccounts: [],
      selectedBrokerAccounts: [],
      selectedFY: null,
      startDate: '',
      endDate: '',
      report: null,
      priceError: null,
      loading: false,
    };
  },
  async created() {
    this.getBrokerAccounts();
  },
  computed: {
    ...mapState({ token: 'token' }),
    financialYears() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      const latestEndYear = currentMonth >= 6 ? currentYear + 1 : currentYear;
      const years = [];
      for (let endYear = latestEndYear; endYear >= latestEndYear - 6; endYear--) {
        years.push('FY' + (endYear - 1).toString().slice(2) + '/' + endYear.toString().slice(2));
      }
      return years;
    },
    sortedRealised() {
      if (!this.report) return [];
      return [...this.report.realised].sort((a, b) => (b.irr ?? -Infinity) - (a.irr ?? -Infinity));
    },
    sortedUnrealised() {
      if (!this.report) return [];
      return [...this.report.unrealised].sort((a, b) => (b.irr ?? -Infinity) - (a.irr ?? -Infinity));
    }
  },
  methods: {
    ...mapMutations(['showSnackbar']),
    onFYSelect(val) {
      if (!val) return;
      const parts = val.replace('FY', '').split('/');
      const startYear = 2000 + parseInt(parts[0]);
      const endYear = 2000 + parseInt(parts[1]);
      this.startDate = startYear + '-07-01';
      this.endDate = endYear + '-06-30';
      this.report = null;
      this.priceError = null;
    },
    async getBrokerAccounts() {
      try {
        const response = await axios.get('/securities/brokeraccounts/', {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        this.brokerAccounts = response.data.map(a => ({
          id: a.id,
          institution: a.institution,
          label: a.institution + ' - ' + a.account_name
        }));
      } catch {
        this.showSnackbar({ message: 'Failed to load broker accounts' });
      }
    },
    async getReport() {
      this.loading = true;
      this.report = null;
      this.priceError = null;
      try {
        const response = await axios.get(
          '/reports/investmentperformance/' + this.selectedBrokerAccounts.join(',') +
          '?start_date=' + this.startDate + '&end_date=' + this.endDate,
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.report = response.data;
      } catch (error) {
        if (error.response?.status === 422 && error.response.data?.error === 'missing_price_data') {
          this.priceError = error.response.data;
        } else {
          this.showSnackbar({ message: 'Failed to load investment performance report' });
        }
      }
      this.loading = false;
    },
    brokerAccountLabel(broker_account_id) {
      const account = this.brokerAccounts.find(a => a.id === broker_account_id);
      return account ? account.institution : broker_account_id;
    },
    formatCurrency(val) {
      if (val == null) return '—';
      return val.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    profitClass(val) {
      if (val == null) return '';
      return val >= 0 ? 'text-green' : 'text-red';
    },
    irrClass(irr) {
      if (irr == null) return '';
      return irr >= 0 ? 'text-green' : 'text-red';
    }
  }
};
</script>

